import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Loader2, Package, MapPin, Calendar, ArrowRight, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MagicAuthPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying | error
  const [error, setError] = useState("");

  useEffect(() => {
    const token = searchParams.get("t");
    if (!token) {
      setError("Ingen magic-link token fundet i URL'en.");
      setStatus("error");
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/magic/verify?t=${encodeURIComponent(token)}`);
        if (res.data?.ok) {
          // Persist to sessionStorage for /min-side to read
          sessionStorage.setItem("magic_customer_email", res.data.customer_email || "");
          sessionStorage.setItem("magic_customer_name", res.data.customer_name || "");
          sessionStorage.setItem("magic_customer_phone", res.data.customer_phone || "");
          sessionStorage.setItem("magic_source", res.data.source || "");
          // Redirect to landing destination
          const dest = res.data.redirect_path || "/min-side";
          navigate(dest, { replace: true });
        }
      } catch (e) {
        setError(e.response?.data?.detail || "Linket kunne ikke verificeres.");
        setStatus("error");
      }
    })();
  }, [searchParams, navigate]);

  if (status === "verifying") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#0A1128] to-[#0D1635] text-white" data-testid="magic-verifying">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-emerald-400" />
          <h2 className="font-display font-bold text-2xl">Logger dig ind...</h2>
          <p className="text-white/60 mt-2">Vent et øjeblik</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#0A1128] to-[#0D1635] text-white p-4" data-testid="magic-error">
      <Card className="bg-white/5 border-white/20 p-8 max-w-md">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="font-display font-bold text-2xl text-center mb-2">Linket kunne ikke bruges</h2>
        <p className="text-white/60 text-center mb-6">{error}</p>
        <Link to="/">
          <Button className="w-full bg-emerald-500 hover:bg-emerald-600">Tilbage til forsiden</Button>
        </Link>
      </Card>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    new: { label: "Afventer", color: "bg-slate-500/20 text-slate-300 border-slate-400/40" },
    confirmed: { label: "Bekræftet", color: "bg-emerald-500/20 text-emerald-300 border-emerald-400/40" },
    in_transit: { label: "Undervejs", color: "bg-blue-500/20 text-blue-300 border-blue-400/40" },
    delivered: { label: "Leveret", color: "bg-emerald-500/30 text-emerald-200 border-emerald-400/50" },
    cancelled: { label: "Annulleret", color: "bg-red-500/20 text-red-300 border-red-400/40" },
  };
  const s = map[status] || { label: status, color: "bg-slate-500/20 text-slate-300" };
  return <Badge className={`${s.color} border font-semibold`}>{s.label}</Badge>;
};

const MyPagePage = () => {
  const [email] = useState(() => sessionStorage.getItem("magic_customer_email") || "");
  const [name] = useState(() => sessionStorage.getItem("magic_customer_name") || "");
  const [source] = useState(() => sessionStorage.getItem("magic_source") || "");
  const [bookings, setBookings] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!email) {
      navigate("/");
      return;
    }
    (async () => {
      try {
        const [bRes, lRes] = await Promise.all([
          axios.get(`${API}/customer/my-bookings?email=${encodeURIComponent(email)}`),
          axios.get(`${API}/customer/loyalty-status?email=${encodeURIComponent(email)}`),
        ]);
        setBookings(bRes.data?.bookings || []);
        setLoyalty(lRes.data || null);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [email, navigate]);

  if (!email) return null;

  const isGold = loyalty?.is_genfind_gold;
  const bookingsToGold = loyalty?.bookings_to_gold ?? 5;

  const bookCta = isGold
    ? "/book?source=genfind&promo=GENFIND_GOLD"
    : (source === "genfind" ? "/book?source=genfind&promo=GENFIND5" : "/book");

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A1128] to-[#0D1635] text-white" data-testid="my-page">
      <div className="container mx-auto px-4 py-12 max-w-5xl">
        <div className="mb-10">
          <h1 className="font-display font-black text-4xl sm:text-5xl mb-2" data-testid="mypage-title">
            Hej {name || "der"} 👋
          </h1>
          <p className="text-white/60">
            Velkommen tilbage{source === "genfind" && <span className="ml-2 text-emerald-300">· via Genfind.eu</span>}
          </p>
          <p className="text-white/40 text-sm mt-1">Logget ind som <code className="text-emerald-300">{email}</code></p>
        </div>

        {/* Loyalty status card */}
        {loyalty && source === "genfind" && (
          <Card className={`mb-8 p-6 ${isGold ? "bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border-yellow-400/50" : "bg-white/5 border-white/20"}`} data-testid="loyalty-card">
            {isGold ? (
              <div className="flex items-center gap-4">
                <div className="text-5xl">🏆</div>
                <div className="flex-1">
                  <h3 className="font-display font-bold text-2xl text-yellow-200">Genfind Gold-medlem</h3>
                  <p className="text-yellow-100/80 text-sm mt-1">
                    Du får automatisk <strong>8% rabat</strong> på alle dine fremtidige bookinger via Genfind.
                  </p>
                </div>
                <Badge className="bg-yellow-500 text-yellow-950 font-bold border-0">8% RABAT</Badge>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display font-bold text-lg">Vej til Genfind Gold 🏆</h3>
                  <span className="text-sm text-white/60">{loyalty.genfind_completed_bookings}/5 leveringer</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-3 mb-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-emerald-500 to-yellow-400 h-full transition-all duration-500"
                    style={{ width: `${Math.min(100, (loyalty.genfind_completed_bookings / 5) * 100)}%` }}
                  ></div>
                </div>
                <p className="text-sm text-white/70">
                  {bookingsToGold === 0
                    ? "Færdiggør din næste levering for at opnå Gold-status!"
                    : `Book og fuldfør ${bookingsToGold} ${bookingsToGold === 1 ? "levering" : "leveringer"} mere for permanent 8% rabat.`}
                </p>
              </div>
            )}
          </Card>
        )}

        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display font-bold text-2xl">Mine leveringer ({bookings.length})</h2>
          <Button
            onClick={() => navigate(bookCta)}
            className="bg-emerald-500 hover:bg-emerald-600"
            data-testid="new-booking-btn"
          >
            Ny booking <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-400" /></div>
        ) : bookings.length === 0 ? (
          <Card className="bg-white/5 border-white/20 p-12 text-center" data-testid="no-bookings">
            <Package className="w-16 h-16 mx-auto mb-4 text-white/30" />
            <h3 className="font-display font-bold text-xl mb-2">Ingen bookinger endnu</h3>
            <p className="text-white/60 mb-6">Lav din første booking nu og spar 5% med GENFIND5</p>
            <Button onClick={() => navigate("/book?source=genfind&promo=GENFIND5")} className="bg-emerald-500 hover:bg-emerald-600">
              Book nu
            </Button>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="bookings-list">
            {bookings.map((b) => (
              <Card key={b.booking_number} className="bg-white/5 backdrop-blur-sm border-white/20 p-6 hover:bg-white/10 transition" data-testid={`booking-${b.booking_number}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="font-mono text-emerald-300 text-sm mb-1">#{b.booking_number}</div>
                    <h3 className="font-display font-bold text-lg">{b.delivery_type === "express" ? "Express" : b.delivery_type === "evening" ? "Aftenlevering" : "Same-day"}</h3>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <StatusBadge status={b.booking_status} />
                    {b.source === "genfind" && <Badge className="bg-emerald-500/30 text-emerald-200 border-emerald-400/40 text-xs">via Genfind</Badge>}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4 text-sm">
                  <div>
                    <div className="text-white/40 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Fra</div>
                    <div className="text-white/90">{b.pickup_address}, {b.pickup_zip} {b.pickup_city}</div>
                  </div>
                  <div>
                    <div className="text-white/40 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Til</div>
                    <div className="text-white/90">{b.delivery_address}, {b.delivery_zip} {b.delivery_city}</div>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-white/10">
                  <div className="text-sm text-white/60 flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> {b.pickup_date} kl. {b.pickup_time}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-display font-bold text-lg text-emerald-300">{Number(b.total_amount_dkk).toFixed(2)} kr</span>
                    {b.payment_status === "paid" && (
                      <a
                        href={`${API}/invoice/${b.id}?email=${encodeURIComponent(email)}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`invoice-${b.booking_number}`}
                      >
                        <Button variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10">
                          📄 Faktura PDF
                        </Button>
                      </a>
                    )}
                    <Link to={`/track?b=${b.booking_number}`}>
                      <Button variant="outline" size="sm" className="border-emerald-400/50 text-emerald-300 hover:bg-emerald-500/10" data-testid={`track-${b.booking_number}`}>
                        Følg pakken
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <div className="text-center mt-12 text-sm text-white/40">
          <Link to="/" className="hover:text-white">← Tilbage til emakurer.dk</Link>
        </div>
      </div>
    </div>
  );
};

export { MagicAuthPage, MyPagePage };
export default MyPagePage;
